import { useCallback, useEffect, useRef, useState } from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { api } from './api-client';

interface VoiceTokenResponse {
  token: string | null;
  identity: string;
  enabled: boolean;
}

export interface StartCallResponse {
  sessionId: string;
  calleeIdentity: string;
  messageId: string;
}

export type CallPhase = 'idle' | 'ringing-out' | 'ringing-in' | 'active';

/**
 * Mirrors `CallStatusBody` on the server.
 *
 * Narrowed rather than `string`: a typo would otherwise compile here and fail as
 * a 400 at the one moment nobody is watching — the end of a call.
 */
export type ReportableCallStatus =
  'ringing' | 'active' | 'completed' | 'missed' | 'declined' | 'failed';

export interface VoiceState {
  /** False when Twilio is unconfigured — the portal hides the call control. */
  enabled: boolean;
  phase: CallPhase;
  /** Seconds since the call connected; 0 until then. */
  elapsedSeconds: number;
  muted: boolean;
  /** Display name of the other party, when we know it. */
  peerName: string | null;
  error: string | null;
}

export interface VoiceActions {
  callConversation(conversationId: string, peerName: string): Promise<void>;
  accept(): void;
  decline(): void;
  hangUp(): void;
  toggleMute(): void;
}

/**
 * Officer-to-officer voice, over the Twilio Voice SDK.
 *
 * The device registers with the officer's `app_user.id` as its identity, so a
 * call is placed to an identity rather than a number — there is no phone number
 * on either side.
 *
 * **The server is told what happened, the browser is not trusted to be the
 * record.** Every transition posts to `/chat/calls/:id/status`, which is what
 * writes the duration and updates the call's entry in the transcript. The
 * browser reports because it knows immediately — `active` the instant audio
 * flows — while Twilio's own callback lands only when the dial resolves. That
 * callback is the backstop, not the primary: it closes the session this hook can
 * never report on, the one where the tab is closed mid-call.
 *
 * Audio only. Twilio Programmable Voice carries no video or screen share.
 */
export function useVoiceCalls(): VoiceState & VoiceActions {
  const [enabled, setEnabled] = useState(false);
  const [phase, setPhase] = useState<CallPhase>('idle');
  const [elapsedSeconds, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [peerName, setPeerName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const device = useRef<Device | null>(null);
  const activeCall = useRef<Call | null>(null);
  const sessionId = useRef<string | null>(null);

  /** Tells the server where the call got to. Never throws into a call handler. */
  const report = useCallback(async (status: ReportableCallStatus, providerCallSid?: string) => {
    const id = sessionId.current;
    if (!id) return;
    try {
      await api.post(`/chat/calls/${id}/status`, { status, providerCallSid });
    } catch {
      // The call itself is unaffected; only the transcript entry goes stale, and
      // failing the hang-up over it would leave the officer stuck in a call.
    }
  }, []);

  // Register the device once, if telephony is configured at all.
  useEffect(() => {
    let disposed = false;
    let created: Device | null = null;

    void (async () => {
      try {
        const { data } = await api.post<VoiceTokenResponse>('/chat/calls/token');
        if (disposed || !data.enabled || !data.token) return;

        created = new Device(data.token, {
          // Chrome throttles background timers, which on a long call degrades
          // audio; the SDK's own worker keeps its clock honest.
          disableAudioContextSounds: false,
        });
        device.current = created;
        setEnabled(true);

        created.on('incoming', (call: Call) => {
          activeCall.current = call;
          // Twilio hands an incoming call an identity and nothing else, so the
          // session id rides along as a custom parameter set by the TwiML. Without
          // it every status this side reports would go nowhere, leaving the call
          // stuck at "ringing" in the transcript forever.
          sessionId.current = call.customParameters?.get('SessionId') ?? null;
          // The socket push that accompanies the call supplies the caller's name;
          // until it lands, show the identity rather than an empty banner.
          setPeerName(call.parameters.From ?? null);
          setPhase('ringing-in');

          call.on('disconnect', () => {
            void report('completed');
            resetCall();
          });
          call.on('cancel', () => {
            // The caller gave up before this side answered.
            void report('missed');
            resetCall();
          });
        });

        created.on('error', (deviceError: { message: string }) => {
          setError(deviceError.message);
        });

        await created.register();
      } catch {
        // An unconfigured or unreachable telephony backend leaves the portal
        // exactly as it was before calling existed.
        if (!disposed) setEnabled(false);
      }
    })();

    return () => {
      disposed = true;
      created?.destroy();
      device.current = null;
    };
    // Mount only: the token outlives a shift and the device must not churn.
  }, [report]);

  // The call clock. Ticks only while connected, so ringing time is not counted.
  useEffect(() => {
    if (phase !== 'active') return;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  function resetCall() {
    activeCall.current = null;
    sessionId.current = null;
    setPhase('idle');
    setElapsed(0);
    setMuted(false);
    setPeerName(null);
  }

  const wire = useCallback(
    (call: Call) => {
      call.on('accept', () => {
        setPhase('active');
        setElapsed(0);
        void report('active', call.parameters.CallSid);
      });
      call.on('disconnect', () => {
        void report('completed');
        resetCall();
      });
      call.on('error', () => {
        void report('failed');
        resetCall();
      });
    },
    [report],
  );

  const callConversation = useCallback(
    async (conversationId: string, name: string) => {
      setError(null);
      const current = device.current;
      if (!current) return;

      try {
        // The server records the call and puts it in the thread first, so one
        // that never connects is still visible to whoever placed it.
        const { data } = await api.post<StartCallResponse>(
          `/chat/conversations/${conversationId}/calls`,
        );
        sessionId.current = data.sessionId;
        setPeerName(name);
        setPhase('ringing-out');

        const call = await current.connect({
          params: { To: data.calleeIdentity, SessionId: data.sessionId },
        });
        activeCall.current = call;
        wire(call);
      } catch {
        setError('That call could not be placed.');
        resetCall();
      }
    },
    [wire],
  );

  const accept = useCallback(() => {
    const call = activeCall.current;
    if (!call) return;
    call.accept();
    setPhase('active');
    setElapsed(0);
    void report('active', call.parameters.CallSid);
  }, [report]);

  const decline = useCallback(() => {
    activeCall.current?.reject();
    void report('declined');
    resetCall();
  }, [report]);

  const hangUp = useCallback(() => {
    activeCall.current?.disconnect();
    // Ending a call that never connected is not a completed call — reporting it
    // as one puts "Call · 0:00" in the transcript where "Missed call" belongs.
    void report(phase === 'active' ? 'completed' : 'missed');
    resetCall();
  }, [report, phase]);

  const toggleMute = useCallback(() => {
    const call = activeCall.current;
    if (!call) return;
    const next = !call.isMuted();
    call.mute(next);
    setMuted(next);
  }, []);

  return {
    enabled,
    phase,
    elapsedSeconds,
    muted,
    peerName,
    error,
    callConversation,
    accept,
    decline,
    hangUp,
    toggleMute,
  };
}
